import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * B&G's Egnyte subdomain. Egnyte is single-tenant per domain — every API and
 * OAuth URL is `https://{domain}.egnyte.com/...` — so the domain is supplied as
 * a shared, tenant-wide env var (provisioned from Key Vault as `EGNYTE_DOMAIN`,
 * e.g. `bandg`), NOT collected per-user. Only each user's OAuth token differs.
 * Mirrors the Quickbase realm-from-env pattern.
 */
export function egnyteDomain(): string | undefined {
  return process.env.EGNYTE_DOMAIN;
}

/**
 * Base URL for every Egnyte REST call (`https://{domain}.egnyte.com`). The
 * File System API lives under `/pubapi/v1/fs` + `/pubapi/v1/fs-content`, OAuth
 * under `/puboauth/token`. Exported so the `@workspace/ai` Egnyte client reuses
 * the same host rather than re-deriving it. Returns a well-formed URL only when
 * `EGNYTE_DOMAIN` is set; the connector is filtered out of the enabled set
 * otherwise (see `isEnabled`), so callers never hit the `undefined` host.
 */
export function egnyteApiBase(): string {
  return `https://${egnyteDomain()}.egnyte.com`;
}

/**
 * Confirm the freshly-issued token can actually reach Egnyte as a real user.
 * `GET /pubapi/v1/userinfo` is the cheapest authenticated endpoint — it returns
 * the connected user's identity and requires only a valid `Egnyte.filesystem`
 * token. A non-200 means the OAuth exchange succeeded but the token can't call
 * the API, which we surface as `status: "error"` with an actionable message
 * instead of a silently-broken "active" connector. Mirrors the Procore `/me`
 * probe.
 */
async function validateEgnyteAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  const domain = egnyteDomain();
  if (!domain) {
    return {
      ok: false,
      message:
        "Egnyte is not configured for this environment (missing domain). " +
        "Contact an administrator.",
    };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`${egnyteApiBase()}/pubapi/v1/userinfo`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (response.ok) {
      return { ok: true };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        ok: false,
        message:
          "Your Egnyte account could not be verified. Make sure you can sign " +
          "in to the Egnyte web app, then reconnect.",
      };
    }

    const body = await response.text().catch(() => "");
    const snippet = body.slice(0, 200).replace(/\s+/g, " ").trim();
    return {
      ok: false,
      message: `Egnyte rejected the connection (${response.status})${snippet ? `: ${snippet}` : "."}`,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `Egnyte did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Could not reach Egnyte: ${detail}` };
  } finally {
    clearTimeout(timeout);
  }
}

export const egnyteConnector: ConnectorDefinition = {
  id: "egnyte",
  name: "Egnyte",
  description: "Browse, download, and manage files in your Egnyte domain",
  icon: "egnyte",
  authType: "oauth",
  kind: "custom",
  oauth: {
    // Egnyte's authorize (GET) and token (POST) endpoints share one URL,
    // `https://{domain}.egnyte.com/puboauth/token`. Built at module load from
    // `EGNYTE_DOMAIN`; when that env var is absent the connector is filtered
    // out via `isEnabled`, so the malformed `https://undefined.egnyte.com`
    // host is never actually used (same gating approach as Procore/Quickbase).
    authorizationUrl: `${egnyteApiBase()}/puboauth/token`,
    tokenUrl: `${egnyteApiBase()}/puboauth/token`,
    // Egnyte.filesystem covers the File System, Search, and Comments/Notes
    // APIs — everything this connector's tools touch. The token is inherently
    // user-scoped: API calls only see files the connecting user can access.
    scopes: ["Egnyte.filesystem"],
    clientIdEnvVar: "EGNYTE_CLIENT_ID",
    clientSecretEnvVar: "EGNYTE_CLIENT_SECRET",
    // Confidential server-side client holding a client_secret — no PKCE.
  },
  // Needs all three: the domain (for URL construction) plus OAuth credentials.
  // When any is absent the connector is filtered out of the enabled set
  // entirely (same pattern as Procore gating on its client id/secret).
  isEnabled: () =>
    Boolean(
      egnyteDomain() &&
        process.env.EGNYTE_CLIENT_ID &&
        process.env.EGNYTE_CLIENT_SECRET
    ),
  validateAccess: validateEgnyteAccess,
};
